import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { ProductFormat } from '../../types/products';

interface FormatEditProps {
  isEditMode: boolean;
  getPrice: (formatId: number) => number | null;
  onPriceChange: (formatId: number, price: number) => void;
  isPriceDirty: (formatId: number) => boolean;
}

interface FormatSelectorProps {
  formats: ProductFormat[];
  selectedFormatId: number | null;
  onChange: (format: ProductFormat) => void;
  editProps?: FormatEditProps;
}

function formatPrice(price: number): string {
  return price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function priceLabel(price: number): string {
  return price === 0 ? 'GRATIS' : `$${formatPrice(price)}`;
}

/** Returns true when content_description doesn't add info beyond the format name/unit */
function isContentRedundant(f: ProductFormat): boolean {
  if (!f.content_description) return true;
  const desc = f.content_description.trim().toLowerCase();
  const name = f.name.trim().toLowerCase();
  const unit = (f.unit_name || '').trim().toLowerCase();
  if (desc === name) return true;
  if (desc === `${f.quantity} ${name}`) return true;
  // Only hide for qty=1 ("1 atado", "1 kg", "1kg"): for qty>1 the
  // description carries the actual count, which is real information.
  if (unit && f.quantity === 1 && (desc === `1 ${unit}` || desc === `1${unit}`)) return true;
  return false;
}

function FormatOptionRow({ f, isSelected, onClick, editProps }: { f: ProductFormat; isSelected: boolean; onClick: () => void; editProps?: FormatEditProps }) {
  const pricePerUnit = f.quantity !== 1 && f.discounted_price > 0
    ? f.discounted_price / f.quantity
    : null;

  const isEditing = editProps?.isEditMode;
  const displayPrice = isEditing ? (editProps.getPrice(f.id) ?? f.price) : f.discounted_price;
  const priceDirty = isEditing && editProps.isPriceDirty(f.id);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-left transition-colors ${
        isSelected
          ? 'bg-green-50 ring-1 ring-green-500'
          : 'hover:bg-gray-50'
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className={`text-base font-medium ${isSelected ? 'text-green-800' : 'text-gray-800'}`}>
          {f.name}
        </div>
        {f.content_description && !isContentRedundant(f) && (
          <div className="mt-1 text-sm text-gray-500">{f.content_description}</div>
        )}
      </div>
      <div className="ml-4 flex-shrink-0 text-right">
        {isEditing ? (
          <EditablePrice
            value={displayPrice}
            onChange={(v) => editProps.onPriceChange(f.id, v)}
            isDirty={!!priceDirty}
          />
        ) : (
          <div className={`text-base font-semibold ${isSelected ? 'text-green-700' : 'text-gray-800'}`}>
            {priceLabel(f.discounted_price)}
          </div>
        )}
        {pricePerUnit && !isEditing && (
          <div className="mt-1 text-sm text-gray-400">
            ${formatPrice(pricePerUnit)}/{f.unit_name || 'u'}
          </div>
        )}
      </div>
      {isSelected && !isEditing && (
        <div className="ml-3 flex-shrink-0">
          <i className="fa fa-check text-base text-green-600" />
        </div>
      )}
    </button>
  );
}

function EditablePrice({ value, onChange, isDirty }: { value: number; onChange: (v: number) => void; isDirty: boolean }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Sync external value when not editing
  useEffect(() => {
    if (!editing) setText(String(value));
  }, [value, editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="1"
        min="0"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const num = parseInt(text, 10);
          if (!isNaN(num) && num >= 0) onChange(num);
          else setText(String(value));
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setText(String(value)); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-24 rounded border border-green-400 bg-white px-2 py-0.5 text-right text-base font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-green-500"
      />
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className={`cursor-text rounded px-1 py-0.5 text-base font-semibold text-gray-800 transition-shadow ${
          isDirty ? 'ring-2 ring-amber-400' : 'ring-1 ring-dashed ring-green-400/60 hover:ring-green-500'
        }`}
      >
        ${formatPrice(value)}
      </span>
      <span className="text-xs font-normal text-gray-400">base</span>
    </span>
  );
}

function FormatSummary({ format, editProps }: { format: ProductFormat; editProps?: FormatEditProps }) {
  const pricePerUnit = format.quantity !== 1 && format.discounted_price > 0
    ? format.discounted_price / format.quantity
    : null;
  return (
    <div className="py-1.5 min-h-[42px] flex flex-col justify-center">
      <div className="text-sm font-medium text-gray-700">
        {format.name}
        {editProps?.isEditMode ? (
          <span className="ml-1.5">
            <EditablePrice
              value={editProps.getPrice(format.id) ?? format.price}
              onChange={(v) => editProps.onPriceChange(format.id, v)}
              isDirty={editProps.isPriceDirty(format.id)}
            />
          </span>
        ) : (
          <span className="ml-1.5 font-semibold text-gray-800">
            {priceLabel(format.discounted_price)}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-xs text-gray-500 min-h-[16px]">
        {format.content_description && !isContentRedundant(format) ? format.content_description : ''}
        {pricePerUnit && (
          <span className={format.content_description && !isContentRedundant(format) ? 'ml-1' : ''}>
            {format.content_description && !isContentRedundant(format) ? '· ' : ''}${formatPrice(pricePerUnit)}/{format.unit_name || 'u'}
          </span>
        )}
        {(isContentRedundant(format) || !format.content_description) && !pricePerUnit && '\u00A0'}
      </div>
    </div>
  );
}

export function FormatSelector({ formats, selectedFormatId, onChange, editProps }: FormatSelectorProps) {
  if (formats.length === 0) return null;

  const selected = formats.find(f => f.id === selectedFormatId) ?? formats[0];

  if (formats.length === 1) {
    return <FormatSummary format={selected} editProps={editProps} />;
  }

  return (
    <>
      {/* Desktop: custom dropdown */}
      <div className="hidden sm:block">
        <DesktopFormatDropdown formats={formats} selected={selected} onChange={onChange} editProps={editProps} />
      </div>

      {/* Mobile: button that opens bottom sheet */}
      <div className="sm:hidden">
        <MobileFormatButton formats={formats} selected={selected} onChange={onChange} />
      </div>
    </>
  );
}

// --- Desktop custom dropdown ---

function DesktopFormatDropdown({
  formats,
  selected,
  onChange,
  editProps,
}: {
  formats: ProductFormat[];
  selected: ProductFormat;
  onChange: (format: ProductFormat) => void;
  editProps?: FormatEditProps;
}) {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Flip dropdown upward when it would overflow the viewport
  useLayoutEffect(() => {
    if (!open) { setFlipUp(false); return; }
    if (!containerRef.current || !dropdownRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - containerRect.bottom;
    if (spaceBelow < dropdownHeight + 8 && containerRect.top > dropdownHeight + 8) {
      setFlipUp(true);
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = useCallback((f: ProductFormat) => {
    onChange(f);
    setOpen(false);
  }, [onChange]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex w-full items-center rounded-lg border bg-gray-50 px-3 py-1.5 text-left transition-colors min-h-[42px] ${
          open ? 'border-green-500 ring-1 ring-green-500' : 'border-green-600 hover:border-green-500'
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-700">
            {selected.name}
            {editProps?.isEditMode ? (
              <span className="ml-1.5">
                <EditablePrice
                  value={editProps.getPrice(selected.id) ?? selected.price}
                  onChange={(v) => editProps.onPriceChange(selected.id, v)}
                  isDirty={editProps.isPriceDirty(selected.id)}
                />
              </span>
            ) : (
              <span className="ml-1.5 font-semibold text-gray-800">
                {priceLabel(selected.discounted_price)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 min-h-[16px]">
            {selected.content_description && !isContentRedundant(selected) ? selected.content_description : ''}
            {selected.quantity !== 1 && selected.discounted_price > 0 && !editProps?.isEditMode && (
              <span className={selected.content_description && !isContentRedundant(selected) ? 'ml-1' : ''}>
                {selected.content_description && !isContentRedundant(selected) ? '· ' : ''}${formatPrice(selected.discounted_price / selected.quantity)}/{selected.unit_name || 'u'}
              </span>
            )}
            {(isContentRedundant(selected) || !selected.content_description) && !(selected.quantity !== 1 && selected.discounted_price > 0) && !editProps?.isEditMode && '\u00A0'}
          </div>
        </div>
        <i className={`fa fa-chevron-down ml-2 flex-shrink-0 text-xs text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={`absolute left-0 right-0 z-50 rounded-lg border border-gray-200 bg-white p-2 shadow-lg space-y-1 ${
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {formats.map(f => (
            <FormatOptionRow
              key={f.id}
              f={f}
              isSelected={f.id === selected.id}
              onClick={() => handleSelect(f)}
              editProps={editProps}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Mobile bottom sheet ---

function MobileFormatButton({
  formats,
  selected,
  onChange,
}: {
  formats: ProductFormat[];
  selected: ProductFormat;
  onChange: (format: ProductFormat) => void;
}) {
  const [open, setOpen] = useState(false);

  const handleSelect = useCallback((format: ProductFormat) => {
    onChange(format);
    setOpen(false);
  }, [onChange]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center rounded-lg border border-green-600 bg-gray-50 px-3 py-2.5 text-left min-h-[42px]"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-700">
            {selected.name}
            <span className="ml-1.5 font-semibold text-gray-800">
              {priceLabel(selected.discounted_price)}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-gray-500 min-h-[16px]">
            {selected.content_description && !isContentRedundant(selected) ? selected.content_description : ''}
            {selected.quantity !== 1 && selected.discounted_price > 0 && (
              <span className={selected.content_description && !isContentRedundant(selected) ? 'ml-1' : ''}>
                {selected.content_description && !isContentRedundant(selected) ? '· ' : ''}${formatPrice(selected.discounted_price / selected.quantity)}/{selected.unit_name || 'u'}
              </span>
            )}
            {(isContentRedundant(selected) || !selected.content_description) && !(selected.quantity !== 1 && selected.discounted_price > 0) && '\u00A0'}
          </div>
        </div>
        <i className="fa fa-chevron-down ml-2 flex-shrink-0 text-xs text-green-500" />
      </button>

      {open && (
        <FormatBottomSheet
          formats={formats}
          selectedId={selected.id}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function FormatBottomSheet({
  formats,
  selectedId,
  onSelect,
  onClose,
}: {
  formats: ProductFormat[];
  selectedId: number;
  onSelect: (format: ProductFormat) => void;
  onClose: () => void;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const currentTranslateY = useRef(0);
  const rafPending = useRef(false);

  // Prevent body scroll while open + block touch events on backdrop
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Prevent touchmove on the entire document while sheet is open
    const preventScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.sheet-scroll-content')) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  // Notify toolbar of sheet height + handle dismiss events
  useEffect(() => {
    const el = sheetRef.current;
    const notify = (h: number) =>
      window.dispatchEvent(new CustomEvent('format-sheet-open', { detail: { height: h } }));

    let ro: ResizeObserver | undefined;
    if (el) {
      ro = new ResizeObserver(([entry]) => {
        notify(entry.contentRect.height);
      });
      ro.observe(el);
    }

    const dismiss = () => onClose();
    window.addEventListener('format-sheet-dismiss', dismiss);

    return () => {
      ro?.disconnect();
      window.removeEventListener('format-sheet-dismiss', dismiss);
      window.dispatchEvent(new Event('format-sheet-close'));
    };
  }, [onClose]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Drag logic
  const handleDragStart = useCallback((clientY: number) => {
    dragStartY.current = clientY;
    currentTranslateY.current = 0;
  }, []);

  const handleDragMove = useCallback((clientY: number) => {
    if (dragStartY.current === null || !sheetRef.current) return;
    const dy = clientY - dragStartY.current;
    if (dy > 0) {
      currentTranslateY.current = dy;
      if (!rafPending.current) {
        rafPending.current = true;
        requestAnimationFrame(() => {
          if (sheetRef.current) {
            sheetRef.current.style.transform = `translateY(${currentTranslateY.current}px)`;
          }
          rafPending.current = false;
        });
      }
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragStartY.current === null || !sheetRef.current) return;
    dragStartY.current = null;

    const sheet = sheetRef.current;

    if (currentTranslateY.current > 80) {
      sheet.style.transition = 'transform 0.2s ease-in';
      sheet.style.transform = 'translateY(100%)';
      const handleTransitionEnd = () => {
        sheet.removeEventListener('transitionend', handleTransitionEnd);
        onClose();
      };
      sheet.addEventListener('transitionend', handleTransitionEnd);
    } else {
      sheet.style.transition = 'transform 0.2s ease-out';
      sheet.style.transform = 'translateY(0)';
      const handleTransitionEnd = () => {
        sheet.removeEventListener('transitionend', handleTransitionEnd);
        sheet.style.transition = '';
      };
      sheet.addEventListener('transitionend', handleTransitionEnd);
    }
  }, [onClose]);

  // Touch event handlers (for iOS PWA)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientY);
    }
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragMove(e.touches[0].clientY);
    }
  }, [handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Mouse event handlers (for desktop)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleDragStart(e.clientY);

    const onMouseMove = (ev: MouseEvent) => {
      handleDragMove(ev.clientY);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      handleDragEnd();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  return (
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 animate-slide-up rounded-t-2xl bg-white shadow-2xl flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Drag handle zone - entire header area */}
        <div
          className="shrink-0 touch-none cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-4 pb-2">
            <div className="h-1 w-12 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-5 pb-4">
            <h3 className="text-lg font-semibold text-gray-800">Seleccionar formato</h3>
            <button
              type="button"
              onClick={onClose}
              onTouchEnd={(e) => e.stopPropagation()}
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Cerrar"
            >
              <i className="fa fa-times text-lg" />
            </button>
          </div>
        </div>

        {/* Format options */}
        <div className="sheet-scroll-content flex-1 overflow-y-auto px-4 py-4 space-y-2">
          {formats.map(f => (
            <FormatOptionRow
              key={f.id}
              f={f}
              isSelected={f.id === selectedId}
              onClick={() => onSelect(f)}
            />
          ))}
        </div>

        {/* Bottom spacing for safe area */}
        <div className="shrink-0" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}
